import { useEffect, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Spin } from "antd";

import styles from "./ProjectsExplorer.module.css";
import defaultLogo from "../../assets/WebTwin.png";
import {
  useGetPublicCategories,
  useGetPublicScenes,
} from "../../api/publicCatalog";
import { useGetBasicSettingsList } from "../../api/settings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark, faPlay } from "@fortawesome/free-solid-svg-icons";
import { CONFIG } from "../../config";
import { useNavbarLogo } from "../../hooks/useNavbarLogo";
import { Footer } from "../../Components/public/Footer";

const ALL_CATEGORY_ID = "all";
const ALL_CATEGORY = {
  id: ALL_CATEGORY_ID,
  name: "All",
  show_in_filter: true,
};

const normalize = value =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

const resolveCategoryByQuery = (categories, queryValue) => {
  if (!queryValue || categories.length === 0) return null;

  return (
    categories.find(item => item?.id?.toString() === queryValue) ||
    categories.find(item => item?.name?.toLowerCase() === queryValue.toLowerCase()) ||
    categories.find(item => normalize(item?.name) === normalize(queryValue)) ||
    null
  );
};

const resolveAssetUrl = path => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${CONFIG.S3_BASE_URI}/${path}`;
};

const compareScenesByOrder = (a, b) =>
  (Number(a?.order) || 0) - (Number(b?.order) || 0);

const resolveSceneImage = scene =>
  scene?.image || scene?.thumbnail || scene?.thumbnail_image || "";

const aggregateScenes = scenes => {
  if (!Array.isArray(scenes) || scenes.length === 0) return [];

  const scenesById = new Map();
  scenes.forEach(scene => {
    if (!scene?.id || scenesById.has(scene.id)) return;
    scenesById.set(scene.id, {
      ...scene,
      image: resolveSceneImage(scene),
    });
  });

  return Array.from(scenesById.values()).sort(compareScenesByOrder);
};

const ProjectsExplorer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageRef = useRef(null);
  const navigate = useNavigate();

  const handleScrollNav = (id) => {
    navigate("/");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const categoryQuery = searchParams.get("category");
  const searchQuery = searchParams.get("search") || "";

  const { data: categoriesData, isLoading: isLoadingCategories } = useGetPublicCategories();
  const { data: publicScenes, isLoading: isLoadingScenes } = useGetPublicScenes();
  const { data: basicSettingList, isLoading: basicSettingLoading } = useGetBasicSettingsList();

  const logoSrc = useNavbarLogo(
    basicSettingList?.navbar_logo,
    basicSettingLoading,
    defaultLogo
  );

  const categories = useMemo(
    () => (Array.isArray(categoriesData) ? categoriesData : []),
    [categoriesData]
  );

  const visibleCategories = useMemo(
    () => categories.filter(item => item?.show_in_filter),
    [categories]
  );

  const filterCategories = useMemo(
    () => [ALL_CATEGORY, ...visibleCategories],
    [visibleCategories]
  );

  const selectedCategory = useMemo(() => {
    const fromQuery = resolveCategoryByQuery(filterCategories, categoryQuery);
    if (fromQuery) return fromQuery;
    return ALL_CATEGORY;
  }, [categoryQuery, filterCategories]);

  useEffect(() => {
    if (!selectedCategory) return;

    const selectedSlug = normalize(selectedCategory?.name);
    if (categoryQuery !== selectedSlug) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("category", selectedSlug);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [categoryQuery, searchParams, selectedCategory, setSearchParams]);

  const allScenes = useMemo(() => {
    const list = Array.isArray(publicScenes) ? publicScenes : [];
    return aggregateScenes(
      list.map(scene => ({
        ...scene,
        categoryName: scene?.categories?.[0]?.name,
      }))
    );
  }, [publicScenes]);

  const selectedCategoryScenes = useMemo(() => {
    if (!selectedCategory || selectedCategory.id === ALL_CATEGORY_ID) return allScenes;
    return allScenes
      .filter(scene =>
        (scene?.categories || []).some(
          category => category?.id === selectedCategory.id
        )
      )
      .map(scene => ({
        ...scene,
        categoryName: selectedCategory.name,
      }));
  }, [allScenes, selectedCategory]);

  const isGlobalSearch = Boolean(searchQuery.trim());

  const activeCategoryScenes = useMemo(() => {
    if (selectedCategory?.id === ALL_CATEGORY_ID) return allScenes;
    return selectedCategoryScenes;
  }, [allScenes, selectedCategory?.id, selectedCategoryScenes]);

  const filteredScenes = useMemo(() => {
    const sourceScenes = isGlobalSearch ? allScenes : activeCategoryScenes;
    if (!isGlobalSearch) return sourceScenes;
    const loweredSearch = searchQuery.toLowerCase();

    return sourceScenes.filter(item => {
      const sceneTitle = item?.title?.toLowerCase() || "";
      const sceneDescription = item?.description?.toLowerCase() || "";
      return (
        sceneTitle.includes(loweredSearch) ||
        sceneDescription.includes(loweredSearch)
      );
    });
  }, [activeCategoryScenes, allScenes, isGlobalSearch, searchQuery]);


  const layoutMode = useMemo(() => {
    if (filteredScenes.length === 1) return "single";
    if (filteredScenes.length === 2) return "dual";
    return "grid";
  }, [filteredScenes.length]);

  const handleCategorySelect = category => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("category", normalize(category?.name));
    setSearchParams(nextSearchParams);
  };

  const handleSearchChange = event => {
    const value = event.target.value;
    const nextSearchParams = new URLSearchParams(searchParams);
    if (value.trim()) {
      nextSearchParams.set("search", value);
    } else {
      nextSearchParams.delete("search");
    }
    setSearchParams(nextSearchParams);
  };

  const handleOpenScene = sceneId => {
    navigate(`/scene/${sceneId}`);
  };

  useEffect(() => {
    const pageElement = pageRef.current;
    if (!pageElement) return;

    const emitScrollState = () => {
      const isScrolled = pageElement.scrollTop > 24;
      window.dispatchEvent(
        new CustomEvent("projects-scroll-state", {
          detail: { isScrolled },
        })
      );
    };

    emitScrollState();
    pageElement.addEventListener("scroll", emitScrollState, { passive: true });

    return () => {
      pageElement.removeEventListener("scroll", emitScrollState);
      window.dispatchEvent(
        new CustomEvent("projects-scroll-state", {
          detail: { isScrolled: false },
        })
      );
    };
    // The scroll container is not mounted while categories are loading, so this
    // must re-run once that loading state clears (a full page reload).
  }, [isLoadingCategories]);

  if (isLoadingCategories) {
    return (
      <section className={styles["projects-loading"]}>
        <Spin />
      </section>
    );
  }

  return (
    <section ref={pageRef} className={styles["projects-page"]}>
      <div className={styles["projects-container"]}>
        <header className={styles["projects-header"]}>
          <h2>Explore Portfolio</h2>
          {/* <p>
            Filter through our interactive spatial environments. Click any project to jump straight into the experience.
          </p> */}
        </header>

        <div className={styles["project-search"]}>
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search portfolio, locations, or categories..."
          />
          {searchQuery && (
            <button
              className={styles["project-search-clear"]}
              onClick={() => {
                const nextSearchParams = new URLSearchParams(searchParams);
                nextSearchParams.delete("search");
                setSearchParams(nextSearchParams);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>

        <div className={styles["filters"]}>
          {filterCategories.map(item => {
            const isActive = selectedCategory?.id === item?.id;
            return (
              <button
                key={item?.id}
                type="button"
                onClick={() => handleCategorySelect(item)}
                className={`${styles["filter-pill"]} ${isActive ? styles.active : ""}`}
              >
                {item?.name}
              </button>
            );
          })}
        </div>



        <section className={styles["projects-results"]}>
          {isLoadingScenes ? (
            <div className={styles["projects-loading-inline"]}>
              <Spin />
            </div>
          ) : (
            <div
              className={`${styles["project-grid"]} ${
                layoutMode === "single"
                  ? styles["project-grid-single"]
                  : layoutMode === "dual"
                  ? styles["project-grid-dual"]
                  : ""
              }`}
            >
              {filteredScenes.map(item => (
                <article
                  key={item?.id}
                  className={styles["project-card"]}
                  onClick={() => handleOpenScene(item?.public_id)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenScene(item?.public_id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className={styles["project-image-wrapper"]}>
                    <img
                      src={resolveAssetUrl(resolveSceneImage(item))}
                      alt={item?.title || "Project"}
                      className={styles["project-image"]}
                    />
                    <div className={styles["project-hover-action"]}>
                      <span><FontAwesomeIcon icon={faPlay} style={{ marginLeft: "4px" }} /></span>
                    </div>
                  </div>
                  <div className={styles["project-content"]}>
                    <h3>{item?.title}</h3>
                    {item?.description && (
                      <p className={styles["project-description"]}>{item.description}</p>
                    )}
                    {item?.categoryName && (
                      <div className={styles["project-tags"]}>
                        <span className={styles["tag"]}>{item.categoryName}</span>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {!isLoadingScenes && filteredScenes.length === 0 && (
          <div className={styles["empty-state"]}>
            <p>No portfolio items matched your filters.</p>
          </div>
        )}
      </div>
      <Footer
        logoSrc={logoSrc}
        onFaqClick={() => handleScrollNav("faq")}
        onContactClick={() => handleScrollNav("contact")}
      />
    </section>
  );
};

export default ProjectsExplorer;
